import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Button,
  TextField,
  Typography,
  Stack,
  InputAdornment,
  Alert,
  CircularProgress,
  IconButton,
} from '@mui/material';
import VpnKeyOutlinedIcon from '@mui/icons-material/VpnKeyOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import apiService from '../../services/apiServices';
import helper from '../../services/helper';
import config from '../../config/config';

const API_URL = process.env.REACT_APP_API_URL || config.baseUrl.replace(/\/$/, '');

const resetPasswordSchema = yup.object().shape({
  newPassword: yup
    .string()
    .min(8, 'Fjalëkalimi duhet të ketë të paktën 8 karaktere')
    .required('Fjalëkalimi i ri është i detyrueshëm'),
  confirmPassword: yup
    .string()
    .oneOf([yup.ref('newPassword')], 'Fjalëkalimet nuk përputhen')
    .required('Konfirmimi është i detyrueshëm'),
});

const ResetPassword = () => {
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(true);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [token, setToken] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(resetPasswordSchema),
    defaultValues: {
      newPassword: '',
      confirmPassword: '',
    },
  });

  useEffect(() => {
    const urlToken = helper().getParameterByName('token');
    if (!urlToken) {
      setError('Token i pavlefshëm ose i skaduar.');
      setValidating(false);
      return;
    }
    // Verifiko token-in me backend
    fetch(`${API_URL}/api/auth/verify-token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: urlToken, tenant: helper().getParameterByName('t') || undefined }),
      })
      .then(r => r.json())
      .then(data => {
        if (data?.success) {
          setToken(urlToken);
        } else {
          setError('Linku ka skaduar ose është i pavlefshëm. Kërko një link të ri.');
        }
      })
      .catch(() => setError('Gabim gjatë verifikimit të linkut.'))
      .finally(() => setValidating(false));
  }, []);

  const onSubmit = async (data) => {
    setLoading(true);
    setError('');

    try {
      // `t` = kompania e linkut: rruga thirret pa hyrje, pra pa tenant.
      const response = await apiService().post('auth/reset-password', {
        token,
        newPassword: data.newPassword,
        tenant: helper().getParameterByName('t') || undefined,
      });

      if (response?.success) {
        setSuccess(true);
        setTimeout(() => {
          window.location.href = '/#/login';
        }, 3000);
      } else {
        setError(response?.error || 'Rivendosja e fjalëkalimit dështoi.');
      }
    } catch (err) {
      setError(err?.message || 'Rivendosja e fjalëkalimit dështoi.');
    } finally {
      setLoading(false);
    }
  };

  if (validating) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: '#f8fafc',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: '#f8fafc',
        p: 3,
      }}
    >
      <Card sx={{ maxWidth: 480, width: '100%' }}>
        <CardContent sx={{ p: 4 }}>
          <Typography variant="h4" fontWeight={700} gutterBottom sx={{ color: '#1e293b' }}>
            Rivendos fjalëkalimin
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Vendosni fjalëkalimin tuaj të ri më poshtë.
          </Typography>

          {success ? (
            <Alert severity="success" sx={{ mb: 2 }}>
              Fjalëkalimi u ndryshua me sukses! Po ridrejtoheni në faqen e login...
            </Alert>
          ) : (
            <form onSubmit={handleSubmit(onSubmit)}>
              <Stack spacing={2.5}>
                {error && <Alert severity="error">{error}</Alert>}

                <TextField
                  fullWidth
                  label="Fjalëkalimi i ri"
                  type={showNew ? 'text' : 'password'}
                  {...register('newPassword')}
                  error={!!errors.newPassword}
                  helperText={errors.newPassword?.message}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <VpnKeyOutlinedIcon color="action" />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowNew(!showNew)} edge="end" size="small">
                          {showNew ? <VisibilityOffOutlinedIcon /> : <VisibilityOutlinedIcon />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <TextField
                  fullWidth
                  label="Konfirmo fjalëkalimin e ri"
                  type={showConfirm ? 'text' : 'password'}
                  {...register('confirmPassword')}
                  error={!!errors.confirmPassword}
                  helperText={errors.confirmPassword?.message}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <VpnKeyOutlinedIcon color="action" />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowConfirm(!showConfirm)}
                          edge="end"
                          size="small"
                        >
                          {showConfirm ? <VisibilityOffOutlinedIcon /> : <VisibilityOutlinedIcon />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={loading || !token}
                  sx={{
                    py: 1.5,
                    fontSize: '1rem',
                    fontWeight: 600,
                    textTransform: 'none',
                    background: 'linear-gradient(135deg, #3E8FA3 0%, #256878 100%)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #4338ca 0%, #6d28d9 100%)',
                    },
                  }}
                >
                  {loading ? 'Duke rivendosur...' : 'Rivendos fjalëkalimin'}
                </Button>

                <Button
                  fullWidth
                  variant="text"
                  onClick={() => (window.location.href = '/#/login')}
                  sx={{ textTransform: 'none' }}
                >
                  Kthehu te Login
                </Button>
              </Stack>
            </form>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default ResetPassword;
